import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { countryCodes, CountryCode } from '../../utils/country-codes';
import * as lpn from 'google-libphonenumber';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-register-oauth',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './register-oauth.component.html',
  styleUrls: ['../register/register.component.css'],
})
export class RegisterOAuthComponent implements OnInit, OnDestroy {
  registerForm!: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  countryCodes: CountryCode[] = countryCodes;
  selectedCountryCode: string = '+389';
  selectedCountry: CountryCode | undefined;
  provider: string = '';
  isMobile = false;
  private phoneUtil = lpn.PhoneNumberUtil.getInstance();
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth <= 768;
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const cleanValue = control.value.replace(/\D/g, '');
      if (cleanValue !== control.value) {
        return { pattern: true };
      }

      try {
        const regionCode = this.getRegionCode(this.selectedCountryCode);
        if (!regionCode) return { invalidNumber: true };

        const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
          control.value,
          regionCode
        );
        if (!this.phoneUtil.isValidNumber(phoneNumber)) {
          return { invalidNumber: true };
        }
        return null;
      } catch (e) {
        return { invalidNumber: true };
      }
    };
  }

  private getRegionCode(countryCode: string): string | null {
    const code = countryCode.replace('+', '');
    const country = this.countryCodes.find((c) => c.dialCode === countryCode);
    return country ? country.code : null;
  }

  ngOnInit(): void {
    this.checkScreenSize();

    const macedoniaCode = this.countryCodes.find((c) => c.code === 'MK');
    if (macedoniaCode) {
      this.selectedCountryCode = macedoniaCode.dialCode;
      this.selectedCountry = macedoniaCode;
    }

    this.initializeForm();
    this.setupFormSubscriptions();
    this.loadOAuthData();
    this.animateFormEntry();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.registerForm = this.formBuilder.group({
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      country_code: [this.selectedCountryCode, [Validators.required]],
      phone_number: [
        '',
        [Validators.pattern(/^\d+$/), this.phoneNumberValidator()],
      ],
      marketing_consent: [false],
    });
  }

  private setupFormSubscriptions(): void {
    this.registerForm
      .get('country_code')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.selectedCountryCode = value;
        this.selectedCountry = this.countryCodes.find(
          (c) => c.dialCode === value
        );

        const phoneControl = this.registerForm.get('phone_number');
        phoneControl?.updateValueAndValidity();
      });
  }

  private loadOAuthData(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const email = params['email'] || '';
        const firstName = params['first_name'] || '';
        const lastName = params['last_name'] || '';
        this.provider = params['provider'] || 'OAuth Provider';

        this.registerForm.patchValue({
          email: email,
          first_name: firstName,
          last_name: lastName,
        });

        this.registerForm.get('email')?.disable();
      });
  }

  private animateFormEntry(): void {
    setTimeout(() => {
      const formElement = document.querySelector('.register-form');
      if (formElement) {
        formElement.classList.add('visible');
      }
    }, 100);
  }

  getPhoneExampleNumber(): string {
    try {
      const regionCode = this.getRegionCode(this.selectedCountryCode);
      if (!regionCode) return '';

      const example = this.phoneUtil.getExampleNumber(regionCode);
      if (!example) return '';

      return this.phoneUtil.format(example, lpn.PhoneNumberFormat.NATIONAL);
    } catch (e) {
      return '';
    }
  }

  formatPhoneNumber(): string | null {
    const countryCode = this.country_code?.value;
    const localNumber = this.phone_number?.value;

    if (!countryCode || !localNumber) {
      return null;
    }

    try {
      const regionCode = this.getRegionCode(countryCode);
      if (!regionCode) return `${countryCode}${localNumber}`;

      const phoneNumber = this.phoneUtil.parseAndKeepRawInput(
        localNumber,
        regionCode
      );
      return this.phoneUtil.format(phoneNumber, lpn.PhoneNumberFormat.E164);
    } catch (e) {
      return `${countryCode}${localNumber}`;
    }
  }

  get first_name() {
    return this.registerForm.get('first_name');
  }

  get last_name() {
    return this.registerForm.get('last_name');
  }

  get email() {
    return this.registerForm.get('email');
  }

  get country_code() {
    return this.registerForm.get('country_code');
  }

  get phone_number() {
    return this.registerForm.get('phone_number');
  }

  get marketing_consent() {
    return this.registerForm.get('marketing_consent');
  }

  private validateAllFormFields(): void {
    Object.keys(this.registerForm.controls).forEach((key) => {
      const control = this.registerForm.get(key);
      if (control && control.enabled) {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  private getEmailValue(): string {
    const rawValue = this.registerForm.getRawValue();
    return rawValue.email || '';
  }

  onSubmit(): void {
    if (this.registerForm.invalid) {
      this.validateAllFormFields();

      if (this.isMobile) {
        const firstErrorElement = document.querySelector('.form-error');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
      return;
    }

    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const userData: any = {
      first_name: this.first_name?.value?.trim(),
      last_name: this.last_name?.value?.trim(),
      email: this.getEmailValue(),
      marketing_consent: this.marketing_consent?.value || false,
    };

    const formattedPhoneNumber = this.formatPhoneNumber();
    if (formattedPhoneNumber) {
      userData.phone_number = formattedPhoneNumber;
    }

    this.authService.registerOAuth(userData).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.successMessage = 'Registration completed! Welcome to Markado!';

        setTimeout(() => {
          this.router.navigate(['/home'], { replaceUrl: true });
        }, 2000);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = this.getErrorMessage(error);

        if (this.isMobile) {
          const errorElement = document.querySelector('.alert-error');
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      },
    });
  }

  private getErrorMessage(error: any): string {
    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.status === 409) {
      return 'An account with this information already exists.';
    }

    if (error?.status === 400) {
      return 'Please check your information and try again.';
    }

    if (error?.status === 401) {
      return 'OAuth authentication failed. Please try logging in again.';
    }

    if (error?.status === 0) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    return 'Registration failed. Please try again.';
  }
}
